import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Phone, Loader2, XCircle, User as UserIcon, Check, Trash2, AlertTriangle, Volume2, Sparkles, Play, Square } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ConsentBlock, SMS_CONSENT_TEXT } from '../components/consent';
import {
  formatUSPhoneAsYouType, sanitizeInternationalPhone, toE164,
} from './signupHelpers';

function StatusPill({ status }) {
  const map = {
    opted_in: { text: 'Opted in', class: 'bg-brand-green text-brand-cream' },
    opted_out: { text: 'Opted out', class: 'bg-brand-cream-dark text-brand-green' },
    dismissed: { text: 'Pending decision', class: 'bg-yellow-100 text-yellow-800' },
    unknown: { text: 'Not decided', class: 'bg-yellow-100 text-yellow-800' },
  };
  const s = map[status] || map.unknown;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${s.class}`}>
      {s.text}
    </span>
  );
}

function OptInPanel({ onOptedIn }) {
  const { authAxios, setSession, token } = useAuth();
  const { toast } = useToast();
  const [phone, setPhone] = useState('');
  const [international, setInternational] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('phone');
  const [phoneMasked, setPhoneMasked] = useState('');
  const [code, setCode] = useState('');

  const startOptIn = useCallback(async (e) => {
    e.preventDefault();
    if (!consent) {
      toast({ title: 'Consent required', description: 'Check the box first.', variant: 'destructive' });
      return;
    }
    const digits = phone.replace(/\D/g, '');
    if ((!international && digits.length !== 10) || (international && digits.length < 7)) {
      toast({ title: 'Enter a valid phone number', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await authAxios.post('/auth/sms/opt-in/start', { phone: toE164(phone, international) });
      setPhoneMasked(data.phone_masked);
      setStep('code');
      toast({ title: 'Code sent', description: `We texted a code to ${data.phone_masked}.` });
    } catch (err) {
      toast({ title: 'Could not send code', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  }, [phone, international, consent, authAxios, toast]);

  const confirmOptIn = useCallback(async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await authAxios.post('/auth/sms/opt-in/confirm', { code, consent_text: SMS_CONSENT_TEXT });
      setSession(null, data);
      toast({ title: 'SMS enabled', description: 'You\u2019re opted in. Opt out any time below.' });
      setStep('phone'); setCode(''); setPhone(''); setConsent(false);
      onOptedIn && onOptedIn();
    } catch (err) {
      toast({ title: 'Verification failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  }, [code, authAxios, setSession, token, toast, onOptedIn]);

  if (step === 'code') {
    return (
      <form onSubmit={confirmOptIn} className="space-y-4">
        <p className="text-sm text-brand-green/70">Enter the code we texted to <strong>{phoneMasked}</strong>.</p>
        <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={10}
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="123456"
          className="w-full text-center tracking-[0.4em] text-2xl font-serif px-4 py-4 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green" />
        <div className="flex gap-3">
          <button type="submit" disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold disabled:opacity-70">
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>) : 'Verify & opt in'}
          </button>
          <button type="button" onClick={() => setStep('phone')}
            className="px-5 py-3 rounded-lg border border-brand-green/20 text-brand-green/80 hover:bg-brand-green/5">Back</button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={startOptIn} className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-brand-green/70">Mobile phone</label>
          <label className="flex items-center gap-2 text-xs text-brand-green/60 cursor-pointer">
            <input type="checkbox" className="accent-brand-green" checked={international}
              onChange={(e) => { setInternational(e.target.checked); setPhone(''); }} />
            International
          </label>
        </div>
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
          <input type="tel" required value={phone}
            onChange={(e) => setPhone(international ? sanitizeInternationalPhone(e.target.value) : formatUSPhoneAsYouType(e.target.value))}
            placeholder={international ? '+44 20 7946 0958' : '(555) 123-4567'}
            className="w-full pl-11 pr-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green" />
        </div>
      </div>
      <div className="p-4 rounded-lg border border-brand-cream-dark bg-brand-cream/60">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1 w-4 h-4 accent-brand-green shrink-0"
            checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span className="text-sm text-brand-green/85 leading-relaxed"><ConsentBlock /></span>
        </label>
      </div>
      <button type="submit" disabled={busy}
        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold disabled:opacity-70">
        {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending code…</>) : (<>Enable SMS <ShieldCheck className="w-4 h-4" /></>)}
      </button>
    </form>
  );
}

function OptedInPanel() {
  const { authAxios, setSession, token, user } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const optOut = useCallback(async () => {
    if (!window.confirm('Opt out of SMS from LISTING AiGENT?')) return;
    setBusy(true);
    try {
      const { data } = await authAxios.post('/auth/sms/opt-out', {});
      setSession(null, data);
      toast({ title: 'Opted out', description: 'You will no longer receive SMS.' });
    } catch (err) {
      toast({ title: 'Could not opt out', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  }, [authAxios, setSession, token, toast]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-brand-green/70">
        SMS is enabled on <strong>{user?.phone}</strong>. You can opt out any time; we will keep a record of when you opted in and when you opted out.
      </p>
      <button type="button" onClick={optOut} disabled={busy}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 font-semibold disabled:opacity-70">
        <XCircle className="w-4 h-4" /> {busy ? 'Working…' : 'Opt out of SMS'}
      </button>
    </div>
  );
}

// ---- Link another account ----
// Shipped Nov 2026 after a realtor hit the "diagnostic looked broken"
// case: their Meta Page binding lived on one email, their daily login
// was another. Backend wraps `POST /api/account/link`, which performs
// a password-verified merge: moves every realtor-keyed collection
// from source → target and deletes the source user row.
function LinkAccount() {
  const { authAxios, refreshMe } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const canSubmit = email.includes('@') && password.length > 0;

  const doLink = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await authAxios.post('/account/link', {
        email: email.trim().toLowerCase(), password,
      });
      const moved = Object.values(data.moves || {})
        .filter(n => typeof n === 'number' && n > 0)
        .reduce((a, b) => a + b, 0);
      toast({
        title: 'Account linked',
        description: `${moved.toLocaleString()} record${moved === 1 ? '' : 's'} moved from ${data.source_email}.`,
      });
      setOpen(false);
      setEmail('');
      setPassword('');
      // Refetch /auth/me so UI picks up any new bindings/listings.
      if (refreshMe) refreshMe();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Please try again.';
      toast({ title: 'Link failed', description: String(detail), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [authAxios, canSubmit, email, password, refreshMe, toast]);

  return (
    <div className="rounded-2xl border border-brand-green/20 bg-brand-cream/40 p-6 mb-6"
         data-testid="link-account-card">
      <div className="flex items-start gap-3">
        <UserIcon className="w-5 h-5 text-brand-green mt-1 flex-shrink-0" />
        <div className="flex-1">
          <h2 className="font-serif text-2xl text-brand-green">
            Link another account
          </h2>
          <p className="text-sm text-brand-green/80 mt-2">
            Have a second login where some of your work lives — a different
            email you used to connect your Facebook Page, say? Fold it in here
            and we'll move its listings, campaigns, and Page connection under
            this account. The other login goes away.
          </p>
          <p className="text-xs text-brand-green/70 mt-2">
            You'll need the email <em>and</em> password of the account you're
            linking. Admin accounts can't be merged, and if the account you're
            linking has an active Stripe subscription you'll need to cancel it
            first.
          </p>
          {!open ? (
            <button
              type="button"
              data-testid="open-link-account"
              onClick={() => setOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-brand-green/40 bg-white text-brand-green px-4 py-2 text-sm font-semibold hover:bg-brand-cream transition-colors"
            >
              <UserIcon className="w-4 h-4" /> Link another account…
            </button>
          ) : (
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-brand-green/80 block mb-1">
                  Email of the account to link
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  data-testid="link-account-email"
                  disabled={busy}
                  placeholder="other@example.com"
                  className="w-full rounded-lg border border-brand-green/30 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-brand-green/80 block mb-1">
                  Password for that account
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  data-testid="link-account-password"
                  disabled={busy}
                  className="w-full rounded-lg border border-brand-green/30 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  data-testid="link-account-cancel"
                  onClick={() => { setOpen(false); setEmail(''); setPassword(''); }}
                  disabled={busy}
                  className="rounded-lg border border-brand-green/30 bg-white text-brand-green px-3 py-1.5 text-sm hover:bg-brand-cream"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="link-account-confirm"
                  onClick={doLink}
                  disabled={!canSubmit || busy}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-green text-brand-cream px-4 py-1.5 text-sm font-semibold hover:opacity-90 disabled:opacity-60"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {busy ? 'Linking…' : 'Link account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ---- Danger zone: permanent account deletion ----
// Wraps `DELETE /api/auth/me`, which does a hard cascade across every
// collection the user owns AND revokes any linked Apple session + releases
// the Twilio number + cancels Stripe (per App Store guideline 5.1.1(v)).
// The type-DELETE-to-confirm modal is the only safeguard between a stray
// tap and a nuked account.
function DangerZone() {
  const { authAxios } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const canConfirm = typed.trim() === 'DELETE';

  const doDelete = useCallback(async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await authAxios.delete('/auth/me');
      toast({ title: 'Account deleted', description: 'Your data has been permanently removed.' });
      // Server cleared the cookies; a hard reload guarantees fresh state.
      window.location.href = '/';
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Please try again.';
      toast({ title: 'Delete failed', description: String(detail), variant: 'destructive' });
      setBusy(false);
    }
  }, [authAxios, canConfirm, toast]);

  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/60 p-6" data-testid="danger-zone">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
        <div className="flex-1">
          <h2 className="font-serif text-2xl text-red-800">Delete account</h2>
          <p className="text-sm text-red-800/80 mt-2">
            Permanently removes your account, listings, clients, appointments,
            messaging history, voice calls, and any linked Apple sign-in. Your
            Twilio number is released and any active subscription is
            cancelled immediately. <strong>This cannot be undone.</strong>
          </p>
          <p className="text-xs text-red-800/80 mt-2">
            <strong>Apple App Store or Google Play subscribers:</strong>{' '}
            deleting your account does <strong>not</strong> cancel your
            subscription. Cancel it in your Apple ID or Google Play
            subscription settings to stop future charges.
          </p>
          <a
            href="/privacy#account-deletion"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="privacy-deletion-policy-link"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-red-800 underline underline-offset-2 hover:text-red-900 transition-colors"
          >
            Read exactly what gets deleted, what's kept (and why), and the full timeline →
          </a>
          <button
            type="button"
            data-testid="open-delete-account"
            onClick={() => { setTyped(''); setOpen(true); }}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" /> Delete account…
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="delete-account-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-serif text-xl text-brand-green">Are you absolutely sure?</h3>
                <p className="text-sm text-brand-green/80 mt-2">
                  This will permanently delete your account and all associated
                  data. To confirm, type <span className="font-mono font-bold text-red-600">DELETE</span> below.
                </p>
              </div>
            </div>
            <input
              type="text"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type DELETE to confirm"
              data-testid="delete-confirm-input"
              disabled={busy}
              className="mt-4 w-full rounded-lg border border-brand-green/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-red-500"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                data-testid="delete-cancel"
                className="px-4 py-2 text-sm font-semibold text-brand-green rounded-lg hover:bg-brand-cream"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={!canConfirm || busy}
                data-testid="delete-confirm"
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-4 py-2 text-sm font-semibold disabled:bg-red-300 disabled:cursor-not-allowed hover:bg-red-700 transition-colors"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? 'Deleting…' : 'Delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user, refreshMe, authAxios, setSession, token } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [savingPron, setSavingPron] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const utteranceRef = useRef(null);
  // Track the last known server value so we can auto-pick up the async
  // Gemini-generated guess a moment after saving a fresh name.
  const [pollUntil, setPollUntil] = useState(0);

  useEffect(() => { setName(user?.name || ''); }, [user?.name]);
  useEffect(() => { setPronunciation(user?.name_pronunciation || ''); }, [user?.name_pronunciation]);

  // Poll `refreshMe` for up to 8s after a name save so the auto-generated
  // pronunciation shows up without the realtor needing to hit reload.
  useEffect(() => {
    if (!pollUntil || Date.now() > pollUntil) return;
    const t = setInterval(() => {
      if (Date.now() > pollUntil) { clearInterval(t); return; }
      refreshMe && refreshMe();
    }, 1500);
    return () => clearInterval(t);
  }, [pollUntil, refreshMe]);

  const saveName = useCallback(async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: 'Enter a display name', variant: 'destructive' });
      return;
    }
    setSavingName(true);
    try {
      const { data } = await authAxios.patch('/auth/me', { name: trimmed });
      setSession(null, data);
      toast({ title: 'Display name saved', description: `We\u2019ll greet you as ${data.name}.` });
      // If pronunciation is currently empty, the server just kicked off the
      // Gemini auto-generate task — poll briefly so the input populates on
      // its own.
      if (!(data.name_pronunciation || '').trim()) {
        setPollUntil(Date.now() + 8000);
      }
    } catch (err) {
      toast({
        title: 'Could not save name',
        description: err?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingName(false);
    }
  }, [name, authAxios, setSession, token, toast]);

  const savePronunciation = useCallback(async (e) => {
    e.preventDefault();
    setSavingPron(true);
    try {
      const { data } = await authAxios.patch('/auth/me', {
        name_pronunciation: pronunciation.trim(),
      });
      setSession(null, data);
      toast({
        title: 'Pronunciation saved',
        description: (data.name_pronunciation || '').trim()
          ? `Your AI assistant will say your name as "${data.name_pronunciation}".`
          : 'Cleared — the AI will use the spelling of your name.',
      });
    } catch (err) {
      toast({
        title: 'Could not save pronunciation',
        description: err?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingPron(false);
    }
  }, [pronunciation, authAxios, setSession, token, toast]);

  const regeneratePron = useCallback(async () => {
    if (!(user?.name || '').trim()) {
      toast({ title: 'Set your display name first', variant: 'destructive' });
      return;
    }
    setRegenerating(true);
    try {
      // Clear the field on the server, then re-save the current name to
      // re-trigger the Gemini auto-generate path.
      await authAxios.patch('/auth/me', { name_pronunciation: '' });
      await authAxios.patch('/auth/me', { name: user.name });
      setPronunciation('');
      setPollUntil(Date.now() + 8000);
      toast({ title: 'Generating pronunciation\u2026', description: 'This takes a couple seconds.' });
    } catch (err) {
      toast({
        title: 'Could not regenerate',
        description: err?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setRegenerating(false);
    }
  }, [user?.name, authAxios, toast]);

  // Stop any in-flight preview when the user leaves the page.
  useEffect(() => () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, []);

  // Browser-side preview of how the AI will say the realtor's name. Uses
  // the OS TTS voice (not the real Gemini Live voice) — labelled as a
  // rough preview in the UI. Speaks the current pronunciation text if
  // set, otherwise falls back to the raw display name so the realtor can
  // hear exactly why they might need a respelling.
  const previewPronunciation = useCallback(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      toast({ title: 'Browser preview not supported here', variant: 'destructive' });
      return;
    }
    if (previewing) {
      window.speechSynthesis.cancel();
      setPreviewing(false);
      return;
    }
    const spoken = (pronunciation || '').trim() || (name || '').trim();
    if (!spoken) {
      toast({ title: 'Nothing to preview yet', description: 'Enter your name first.' });
      return;
    }
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(`Hi, this is ${spoken}.`);
    // Emma-ish preview hint — warm & friendly, matches the default Aoede
    // voice we ship with. Real call voice is a richer Gemini Live voice.
    utt.pitch = 1.15;
    utt.rate = 0.95;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) => {
      const n = (v.name || '').toLowerCase();
      return /(female|samantha|karen|victoria|zira|susan|allison)/.test(n);
    });
    if (preferred) utt.voice = preferred;
    utt.onend = () => setPreviewing(false);
    utt.onerror = () => setPreviewing(false);
    utteranceRef.current = utt;
    setPreviewing(true);
    window.speechSynthesis.speak(utt);
  }, [pronunciation, name, previewing, toast]);

  if (!user) return null;

  return (
    <div className="bg-brand-cream min-h-screen">
      <section className="bg-brand-green text-brand-cream py-14">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Account</span>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">Settings</h1>
          <p className="mt-3 text-brand-cream/75">Signed in as {user.email}</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 space-y-6">

          <div className="bg-white rounded-2xl border border-brand-cream-dark p-8">
            <div className="flex items-center gap-3">
              <UserIcon className="w-5 h-5 text-brand-green" />
              <h2 className="font-serif text-2xl text-brand-green">Display name</h2>
            </div>
            <p className="mt-2 text-sm text-brand-green/70">
              This is how we greet you in the navigation bar and inside the app.
            </p>
            <form onSubmit={saveName} className="mt-5 flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                data-testid="settings-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={64}
                placeholder="e.g. Alex Rivera"
                className="flex-1 px-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
              />
              <button
                type="submit"
                disabled={savingName}
                data-testid="settings-name-save"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold disabled:opacity-70"
              >
                {savingName ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : (<><Check className="w-4 h-4" /> Save</>)}
              </button>
            </form>

            {/* Name Pronunciation — Gemini 3 Flash auto-generates a phonetic
                respelling on name save; the realtor can edit or regenerate. */}
            <div className="mt-6 pt-6 border-t border-brand-cream-dark">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-brand-green" />
                <h3 className="font-serif text-lg text-brand-green">Name pronunciation</h3>
              </div>
              <p className="mt-2 text-sm text-brand-green/70">
                A phonetic respelling your AI voice assistant will use when it
                says your name on calls. We&rsquo;ll generate a guess for you;
                edit it any time. Use hyphens between syllables and CAPS for
                the stressed syllable (e.g. <em>en-GOH-zee</em>).
              </p>
              <form onSubmit={savePronunciation} className="mt-4 flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  data-testid="settings-pronunciation-input"
                  value={pronunciation}
                  onChange={(e) => setPronunciation(e.target.value)}
                  maxLength={80}
                  placeholder={pollUntil && Date.now() < pollUntil
                    ? 'Generating…'
                    : 'e.g. AL-eks ri-VAIR-uh'}
                  className="flex-1 px-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                />
                <button
                  type="button"
                  onClick={previewPronunciation}
                  disabled={!pronunciation.trim() && !name.trim()}
                  data-testid="settings-pronunciation-preview"
                  title={previewing ? 'Stop preview' : 'Hear a rough browser preview of your name'}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-brand-green/30 text-brand-green hover:bg-brand-green/5 disabled:opacity-60"
                >
                  {previewing
                    ? <Square className="w-4 h-4" />
                    : <Play className="w-4 h-4" />}
                  <span className="hidden sm:inline">{previewing ? 'Stop' : 'Preview'}</span>
                </button>
                <button
                  type="button"
                  onClick={regeneratePron}
                  disabled={regenerating || savingPron || !name.trim()}
                  data-testid="settings-pronunciation-regenerate"
                  title="Ask the AI to suggest a new phonetic spelling"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-brand-green/30 text-brand-green hover:bg-brand-green/5 disabled:opacity-60"
                >
                  {regenerating
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Sparkles className="w-4 h-4" />}
                  <span className="hidden sm:inline">Regenerate</span>
                </button>
                <button
                  type="submit"
                  disabled={savingPron}
                  data-testid="settings-pronunciation-save"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold disabled:opacity-70"
                >
                  {savingPron ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : (<><Check className="w-4 h-4" /> Save</>)}
                </button>
              </form>
              <p className="mt-2 text-xs text-brand-green/50">
                Preview uses your browser&rsquo;s built-in voice — the real call voice is a richer Gemini Live voice.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-brand-cream-dark p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-brand-green">Text messages (SMS)</h2>
              <StatusPill status={user.sms_opt_in_status} />
            </div>
            <p className="mt-2 text-sm text-brand-green/70">
              Opting in is <strong>optional</strong> and never required to use LISTING AiGENT.
            </p>
            <div className="mt-6">
              {user.sms_opted_in
                ? <OptedInPanel />
                : <OptInPanel onOptedIn={refreshMe} />}
            </div>

            <LinkAccount />
            <DangerZone />
          </div>
        </div>
      </section>
    </div>
  );
}
